import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ListChecks,
  Warning,
  ClockCountdown,
  CheckCircle,
  Eye,
  Prohibit,
  ChatDots,
  ArrowsClockwise,
  CalendarBlank,
  TrendUp,
  Bell,
  ShieldCheck,
  Database,
} from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const CARD_DEFS = [
  {
    key: "open",
    label: "Open Tasks",
    icon: ListChecks,
    color: "text-blue-400",
  },
  {
    key: "due_today",
    label: "Due Today",
    icon: CalendarBlank,
    color: "text-emerald-400",
  },
  { key: "overdue", label: "Overdue", icon: Warning, color: "text-red-400" },
  {
    key: "awaiting_review",
    label: "Awaiting Review",
    icon: Eye,
    color: "text-violet-400",
  },
  {
    key: "completed_today",
    label: "Completed Today",
    icon: CheckCircle,
    color: "text-emerald-400",
  },
  { key: "blocked", label: "Blocked", icon: Prohibit, color: "text-red-400" },
  {
    key: "no_feedback",
    label: "No Feedback",
    icon: ChatDots,
    color: "text-amber-400",
  },
  {
    key: "extension_requests",
    label: "Extension Requests",
    icon: ClockCountdown,
    color: "text-amber-400",
  },
  {
    key: "not_acknowledged",
    label: "Not Acknowledged",
    icon: Eye,
    color: "text-zinc-400",
  },
  {
    key: "reopened",
    label: "Reopened",
    icon: ArrowsClockwise,
    color: "text-amber-400",
  },
];

// "My Jobs" — a personal, order-centric breakdown for a team member's own Dashboard (separate
// from the task-centric CARD_DEFS above). A "Job" = an Order this member is a stage assignee on.
const JOB_CARD_DEFS = [
  {
    key: "pending",
    label: "Pending Jobs",
    icon: ListChecks,
    color: "text-blue-400",
  },
  {
    key: "in_time",
    label: "Jobs In Time",
    icon: CheckCircle,
    color: "text-emerald-400",
  },
  {
    key: "delayed",
    label: "Jobs Delayed",
    icon: Warning,
    color: "text-red-400",
  },
  {
    key: "inqueue",
    label: "Inqueue",
    icon: ClockCountdown,
    color: "text-amber-400",
  },
  {
    key: "history",
    label: "Job History (Closed)",
    icon: CalendarBlank,
    color: "text-zinc-400",
  },
  { key: "all", label: "All Jobs", icon: TrendUp, color: "text-emerald-400" },
];

const CHART_COLORS = [
  "#128a49",
  "#d9e1d5",
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
];

// Admin-only "at a glance" counts for the merged Admin nav section (Access Requests,
// Permissions, Masters, Company Settings) — surfaced right on the Dashboard.
const ADMIN_CARD_DEFS = [
  {
    key: "pending_access_requests",
    label: "Pending Access Requests",
    icon: Bell,
    color: "text-amber-400",
    to: "/access-requests",
  },
  {
    key: "custom_permission_users",
    label: "Custom Permissions Set",
    icon: ShieldCheck,
    color: "text-emerald-400",
    to: "/permissions",
  },
  {
    key: "masters_total_items",
    label: "Masters Items Configured",
    icon: Database,
    color: "text-blue-400",
    to: "/masters",
  },
  {
    key: "order_takers_configured",
    label: "Order Takers Configured",
    icon: CheckCircle,
    color: "text-emerald-400",
    to: "/settings",
  },
];

function Panel({ title, children, className = "" }) {
  return (
    <div className={`bg-zinc-900/50 border border-zinc-800 ${className}`}>
      <div className="px-5 py-3 border-b border-zinc-800 text-xs font-bold uppercase tracking-[0.15em] text-zinc-400">
        {title}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

const ACCENT_BAR = {
  "text-blue-400": "bg-blue-500",
  "text-emerald-400": "bg-emerald-500",
  "text-red-400": "bg-rose-500",
  "text-violet-400": "bg-violet-500",
  "text-amber-400": "bg-amber-500",
  "text-zinc-400": "bg-zinc-500",
};

function KPICard({ def, value, onClick, testId }) {
  const Icon = def.icon;
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="bg-white rounded-md overflow-hidden text-left shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all group"
    >
      <div
        className={`h-1 w-full ${ACCENT_BAR[def.color] || "bg-emerald-500"}`}
      />
      <div className="p-3.5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 leading-tight">
            {def.label}
          </span>
          <Icon
            size={16}
            className={`${def.color.replace("400", "600")} opacity-80 group-hover:opacity-100`}
          />
        </div>
        <div className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-slate-900 tabular-nums">
          {value}
        </div>
      </div>
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [myJobs, setMyJobs] = useState(null);
  const [jobsDialogKey, setJobsDialogKey] = useState(null);
  const [adminOverview, setAdminOverview] = useState(null);

  useEffect(() => {
    api
      .get("/dashboard")
      .then((r) => setData(r.data))
      .catch(() => {});
    api
      .get("/tasks?view=my")
      .then((r) => setTasks(r.data))
      .catch(() => {});
    if (!["admin", "manager"].includes(user.role)) {
      api
        .get("/dashboard/my-jobs")
        .then((r) => setMyJobs(r.data))
        .catch(() => {});
    }
    if (user.role === "admin") {
      api
        .get("/dashboard/admin-overview")
        .then((r) => setAdminOverview(r.data))
        .catch(() => {});
    }
  }, [user.role]);

  if (!data)
    return (
      <div className="p-8 text-zinc-500 font-mono text-sm">
        Loading dashboard…
      </div>
    );

  const { cards, charts } = data;
  const urgent = [...tasks]
    .filter((t) => !["completed", "cancelled", "approved"].includes(t.status))
    .sort((a, b) => new Date(a.due_at || 8e15) - new Date(b.due_at || 8e15))
    .slice(0, 6);

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] animate-fade-in">
      <div
        className="relative overflow-hidden rounded-lg bg-gradient-to-r from-emerald-950 via-teal-900 to-slate-900 border border-emerald-700/50 px-6 py-5 mb-6 shadow-lg"
        data-testid="dashboard-hero-banner"
      >
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-bold mb-1.5 bg-emerald-500/20 border border-emerald-500/40 rounded-full px-2.5 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {["admin", "manager"].includes(user.role)
                ? "Mission Control — Live"
                : "My Workspace — Live"}
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
              {["admin", "manager"].includes(user.role)
                ? "Mission Control Overview"
                : "My Dashboard"}
            </h1>
            <p className="text-sm text-emerald-100/70 mt-1">
              Every job, PO and task — one control surface.
            </p>
          </div>
          <div className="text-xs font-mono text-emerald-100/60">
            {new Date().toLocaleString("en-GB", {
              weekday: "long",
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </div>
        </div>
      </div>

      {user.role === "admin" && adminOverview && (
        <div className="mb-6" data-testid="admin-overview-panel">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400 mb-2">
            Admin Overview
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ADMIN_CARD_DEFS.map((c) => (
              <KPICard
                key={c.key}
                def={c}
                value={adminOverview[c.key]}
                onClick={() => navigate(c.to)}
                testId={`admin-stat-${c.key}`}
              />
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {CARD_DEFS.map((c) => (
          <KPICard
            key={c.key}
            def={c}
            value={cards[c.key]}
            onClick={() => navigate("/tasks")}
            testId={`stat-${c.key}`}
          />
        ))}
      </div>

      {!["admin", "manager"].includes(user.role) && myJobs && (
        <>
          <div className="mb-6">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400 mb-2">
              My Jobs
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {JOB_CARD_DEFS.map((c) => (
                <KPICard
                  key={c.key}
                  def={c}
                  value={myJobs.counts[c.key]}
                  onClick={() => setJobsDialogKey(c.key)}
                  testId={`job-stat-${c.key}`}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <Panel
              title="My Job Performance — Last 6 Months"
              className="lg:col-span-2"
            >
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={myJobs.performance}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#27272a"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: "#a1a1aa", fontSize: 11 }}
                    axisLine={{ stroke: "#3f3f46" }}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fill: "#a1a1aa", fontSize: 11 }}
                    axisLine={{ stroke: "#3f3f46" }}
                    allowDecimals={false}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    domain={[0, 100]}
                    tick={{ fill: "#a1a1aa", fontSize: 11 }}
                    axisLine={{ stroke: "#3f3f46" }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#18181b",
                      border: "1px solid #3f3f46",
                      borderRadius: 2,
                      color: "#fafafa",
                    }}
                    cursor={{ fill: "#10b98114" }}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="completed"
                    name="Jobs Completed"
                    fill="#10b981"
                    radius={[2, 2, 0, 0]}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="on_time_pct"
                    name="On-Time %"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={{ fill: "#22c55e", r: 3 }}
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Panel>
            <Panel title="Overall On-Time Delivery">
              <div className="flex flex-col items-center justify-center h-[220px]">
                <TrendUp size={40} className="text-emerald-500 mb-3" />
                <div
                  className="font-heading text-6xl font-black text-zinc-50"
                  data-testid="my-jobs-on-time-pct"
                >
                  {myJobs.overall_on_time_pct}%
                </div>
                <div className="text-xs text-zinc-500 mt-2 uppercase tracking-wider">
                  Jobs delivered on time
                </div>
              </div>
            </Panel>
          </div>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Panel title="Completed vs Pending">
          {charts.completed_vs_pending.every((d) => d.value === 0) ? (
            <div className="h-[220px] flex items-center justify-center text-sm text-zinc-500">
              No tasks yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={charts.completed_vs_pending}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={2}
                >
                  {charts.completed_vs_pending.map((e, i) => (
                    <Cell
                      key={i}
                      fill={i === 0 ? "#128a49" : "#d9e1d5"}
                      stroke="none"
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "#ffffff",
                    border: "1px solid #dde4d9",
                    borderRadius: 2,
                    color: "#17251c",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex justify-center gap-6 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 bg-emerald-600" />
              Completed
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 bg-[#d9e1d5]" />
              Pending
            </span>
          </div>
        </Panel>

        <Panel title="Department Workload" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts.department_workload}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#dde4d9"
                vertical={false}
              />
              <XAxis
                dataKey="name"
                tick={{ fill: "#55655a", fontSize: 11 }}
                axisLine={{ stroke: "#dde4d9" }}
              />
              <YAxis
                tick={{ fill: "#55655a", fontSize: 11 }}
                axisLine={{ stroke: "#dde4d9" }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: "#ffffff",
                  border: "1px solid #dde4d9",
                  borderRadius: 2,
                  color: "#17251c",
                }}
                cursor={{ fill: "#128a4914" }}
              />
              <Bar dataKey="value" fill="#128a49" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Panel title="7-Day Completion Trend" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={charts.completion_trend}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#dde4d9"
                vertical={false}
              />
              <XAxis
                dataKey="day"
                tick={{ fill: "#55655a", fontSize: 11 }}
                axisLine={{ stroke: "#dde4d9" }}
              />
              <YAxis
                tick={{ fill: "#55655a", fontSize: 11 }}
                axisLine={{ stroke: "#dde4d9" }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: "#ffffff",
                  border: "1px solid #dde4d9",
                  borderRadius: 2,
                  color: "#17251c",
                }}
              />
              <Line
                type="monotone"
                dataKey="completed"
                stroke="#128a49"
                strokeWidth={2}
                dot={{ fill: "#128a49", r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="On-Time Completion">
          <div className="flex flex-col items-center justify-center h-[220px]">
            <div className="relative">
              <TrendUp size={40} className="text-emerald-600 mb-3 mx-auto" />
              <div className="font-heading text-6xl font-black text-zinc-50">
                {charts.on_time_pct}%
              </div>
            </div>
            <div className="text-xs text-zinc-500 mt-2 uppercase tracking-wider">
              Delivered on time
            </div>
          </div>
        </Panel>
      </div>

      {data.orders && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <Panel title="Order Pipelines" className="lg:col-span-2">
            <div className="divide-y divide-zinc-800 -mx-5 -my-5">
              {data.orders.list.length === 0 ? (
                <div className="px-5 py-6 text-sm text-zinc-400">
                  No active orders. Start one from the Orders page.
                </div>
              ) : (
                data.orders.list.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => navigate(`/orders/${o.id}`)}
                    data-testid={`dash-order-${o.id}`}
                    className="w-full flex items-center gap-4 px-5 py-3 hover:bg-zinc-900 transition-colors text-left"
                  >
                    <span className="font-mono text-[11px] text-emerald-600 w-28 shrink-0">
                      {o.order_no}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-100 truncate">
                        {o.name}
                      </div>
                      <div className="text-xs text-zinc-400 truncate">
                        ▶ {o.current_stage || "—"}
                      </div>
                    </div>
                    {o.stuck && (
                      <span className="text-[10px] uppercase tracking-wider bg-red-500/10 text-red-400 border border-red-500/20 px-1.5 py-0.5 rounded-sm">
                        {o.reason}
                      </span>
                    )}
                    <div className="w-24 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-600"
                          style={{ width: `${o.progress}%` }}
                        />
                      </div>
                      <span className="text-[11px] font-mono text-zinc-400 w-8">
                        {o.progress}%
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </Panel>
          <Panel title="Stuck · Needs Attention">
            {data.orders.stuck.length === 0 ? (
              <div className="h-[120px] flex items-center justify-center text-sm text-emerald-400/80">
                All orders on track
              </div>
            ) : (
              <div className="space-y-2">
                {data.orders.stuck.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => navigate(`/orders/${o.id}`)}
                    data-testid={`stuck-${o.id}`}
                    className="w-full text-left border border-red-500/20 bg-red-500/5 p-3 hover:border-red-500/40 transition-colors"
                  >
                    <div className="text-sm text-zinc-100 truncate">
                      {o.name}
                    </div>
                    <div className="text-xs text-red-400 mt-0.5">
                      {o.reason} · {o.current_stage}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}

      {data.pending_pos && (
        <Panel title="Pending POs — Awaiting Material" className="mb-6">
          {data.pending_pos.length === 0 ? (
            <div
              className="text-sm text-emerald-400/80 py-4 text-center"
              data-testid="pending-pos-empty"
            >
              No pending purchase orders — all material received.
            </div>
          ) : (
            <div
              className="divide-y divide-zinc-800 -mx-5 -my-5"
              data-testid="pending-pos-list"
            >
              {data.pending_pos.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate("/purchase-orders")}
                  data-testid={`pending-po-${p.id}`}
                  className="w-full flex items-center gap-4 px-5 py-3 hover:bg-zinc-900 transition-colors text-left"
                >
                  <span className="font-mono text-[11px] text-emerald-600 w-20 shrink-0">
                    {p.po_no}
                  </span>
                  <span className="flex-1 text-sm text-zinc-100 truncate">
                    {p.vendor_name}
                  </span>
                  <span
                    className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border shrink-0 ${p.status === "sent" ? "bg-blue-500/10 text-blue-400 border-blue-500/30" : "bg-amber-500/10 text-amber-400 border-amber-500/40"}`}
                  >
                    {p.status === "sent" ? "Sent" : "Accepted"}
                  </span>
                  {p.days_pending != null && (
                    <span
                      className={`text-xs font-mono shrink-0 ${p.days_pending > 7 ? "text-red-400" : "text-zinc-400"}`}
                    >
                      {p.days_pending}d pending
                    </span>
                  )}
                  <span className="font-mono text-xs text-zinc-300 w-24 text-right shrink-0">
                    ₹ {Number(p.grand_total || 0).toLocaleString("en-IN")}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Panel>
      )}

      <Panel title="Priority Tasks — Act Now">
        {urgent.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">
            No active tasks. You're all caught up.
          </div>
        ) : (
          <div className="divide-y divide-zinc-800 -mx-5 -my-5">
            {urgent.map((t) => (
              <button
                key={t.id}
                onClick={() => navigate(`/tasks/${t.id}`)}
                data-testid={`urgent-task-${t.id}`}
                className="w-full flex items-center gap-4 px-5 py-3 hover:bg-zinc-900 transition-colors text-left"
              >
                <span className="font-mono text-[11px] text-zinc-500 w-28 shrink-0 truncate">
                  {t.ref_code || t.customer_name || "—"}
                </span>
                <span className="flex-1 text-sm text-zinc-100 truncate">
                  {t.title}
                </span>
                <span
                  className={`text-xs font-mono ${t.is_overdue ? "text-red-400" : "text-zinc-400"}`}
                >
                  {t.is_overdue
                    ? `Overdue ${t.time.delay}`
                    : t.time.remaining
                      ? `${t.time.remaining} left`
                      : "—"}
                </span>
              </button>
            ))}
          </div>
        )}
      </Panel>

      {myJobs && (
        <Dialog
          open={!!jobsDialogKey}
          onOpenChange={(v) => !v && setJobsDialogKey(null)}
        >
          <DialogContent
            className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-lg"
            data-testid="jobs-list-dialog"
          >
            <DialogHeader>
              <DialogTitle className="font-heading">
                {JOB_CARD_DEFS.find((c) => c.key === jobsDialogKey)?.label}
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                {(myJobs.lists[jobsDialogKey] || []).length} job(s)
              </DialogDescription>
            </DialogHeader>
            <div className="divide-y divide-zinc-800 max-h-[420px] overflow-y-auto -mx-6">
              {(myJobs.lists[jobsDialogKey] || []).length === 0 ? (
                <div className="px-6 py-6 text-sm text-zinc-500 text-center">
                  Nothing here.
                </div>
              ) : (
                myJobs.lists[jobsDialogKey].map((j) => (
                  <button
                    key={j.id}
                    onClick={() => navigate(`/orders/${j.id}`)}
                    data-testid={`job-row-${j.id}`}
                    className="w-full flex items-center gap-3 px-6 py-3 hover:bg-zinc-900 transition-colors text-left"
                  >
                    <span className="font-mono text-[11px] text-emerald-600 w-24 shrink-0">
                      {j.order_no}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-100 truncate">
                        {j.name}
                      </div>
                      <div className="text-xs text-zinc-400 truncate">
                        ▶{" "}
                        {j.current_stage ||
                          (j.is_completed
                            ? "Completed"
                            : j.started
                              ? "In progress"
                              : "Not started")}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {j.overdue && (
                        <span className="text-[10px] uppercase tracking-wider bg-red-500/10 text-red-400 border border-red-500/20 px-1.5 py-0.5 rounded-sm">
                          Delayed
                        </span>
                      )}
                      {j.is_responsible && (
                        <span
                          className="text-[10px] uppercase tracking-wider bg-emerald-600/10 text-emerald-400 border border-emerald-600/20 px-1.5 py-0.5 rounded-sm"
                          data-testid={`job-role-responsible-${j.id}`}
                        >
                          Job Responsibility
                        </span>
                      )}
                      {j.is_stage_assignee && (
                        <span
                          className="text-[10px] uppercase tracking-wider bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.5 rounded-sm"
                          data-testid={`job-role-assignee-${j.id}`}
                        >
                          Stage Assigned
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
